import { useEffect, useRef } from "react";
import { dateTimeToString } from "@/shared/utils/Dates";
import { Transcript } from "../models/Transcript";
import { useGetTransciptSource } from "@/features/sources/api/useGetTransciptSource";

interface TranscriptContentProps {
  transcript: Transcript;
  chunkId?: number;
}

const removeSpeakerNameFromChunk = (chunk: string, speakerName?: string) => {
  if (!speakerName) return chunk;
  const prefix = `${speakerName}: `;
  return chunk.startsWith(prefix) ? chunk.substring(prefix.length) : chunk;
};

export default function TranscriptContent({
  transcript,
  chunkId,
}: TranscriptContentProps) {
  const targetComponentIdRef = useRef<number>(-1);
  const chunkStartIndexRef = useRef<number>(-1);
  const { source, getTranscriptSource } = useGetTransciptSource();

  useEffect(() => {
    if (chunkId) {
      getTranscriptSource(transcript.id, chunkId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chunkId]);

  useEffect(() => {
    if (!source) return;

    const chunkText = source.content;
    if (chunkText) {
      for (const component of transcript.components) {
        const adjustedChunkText = removeSpeakerNameFromChunk(
          chunkText,
          component.speakerName,
        );
        if (component.text && component.text.includes(adjustedChunkText)) {
          targetComponentIdRef.current = component.id;
          chunkStartIndexRef.current =
            component.text.indexOf(adjustedChunkText);

          // Find and scroll to the highlighted element
          setTimeout(() => {
            const highlightedElement =
              document.getElementById("marked-chunk") ||
              document.getElementById(`transcript-component-${component.id}`);
            if (highlightedElement) {
              highlightedElement.scrollIntoView({
                behavior: "auto",
                block: "center",
              });
            }
          });
          break;
        }
      }
    }

    return () => {
      targetComponentIdRef.current = -1;
      chunkStartIndexRef.current = -1;
    };
  }, [source, transcript.components]);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold tracking-tight">
          {transcript.title}
        </h2>
        <p className="text-sm text-muted-foreground">
          {dateTimeToString(transcript.transcriptDate)}
        </p>
      </div>

      <div className="space-y-6">
        {transcript.components.map((component) => (
          <div key={component.id} className="space-y-2">
            <div className="font-medium text-foreground">
              {component.speakerName}
            </div>
            <div
              id={`transcript-component-${component.id}`}
              className="text-sm leading-relaxed"
            >
              {component.id === targetComponentIdRef.current &&
              component.text &&
              chunkStartIndexRef.current !== -1 ? (
                <>
                  {component.text.substring(0, chunkStartIndexRef.current)}
                  <mark
                    className="bg-yellow-200 dark:bg-yellow-800"
                    id={"marked-chunk"}
                  >
                    {source?.content &&
                      removeSpeakerNameFromChunk(
                        source.content,
                        component.speakerName,
                      )}
                  </mark>
                  {component.text.substring(
                    chunkStartIndexRef.current +
                      (source?.content
                        ? removeSpeakerNameFromChunk(
                            source.content,
                            component.speakerName,
                          ).length
                        : 0),
                  )}
                </>
              ) : (
                component.text
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
